import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Brand } from '../components/Layout/Wordmark';
import { Button, Field, IconButton, InlineBanner, Input, LanguageSwitcher, ThemeSwitch } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';
import api, { getApiError } from '../services/api';
import type { RootState } from '../store';
import { setCredentials } from '../store/slices/authSlice';
import type { User } from '../types/User';

interface LoginResponse {
    user: User;
    access_token: string;
    refresh_token: string;
}

type Failure = 'invalid' | 'network' | 'other';
type Errors = Partial<Record<'email' | 'password', string>>;

const Login = () => {
    const { t } = useTranslation('auth');
    usePageTitle(t('login.pageTitle'));
    const dispatch = useDispatch();
    const session = useSelector((s: RootState) => s.auth);
    const form = useRef<HTMLFormElement>(null);
    const email = useRef<HTMLInputElement>(null);
    const password = useRef<HTMLInputElement>(null);
    const [submitting, setSubmitting] = useState(false);
    const [shown, setShown] = useState(false);
    const [failure, setFailure] = useState<Failure | null>(null);
    const [errors, setErrors] = useState<Errors>({});

    if (session.isAuthenticated) return <Navigate to={session.user?.role?.name === 'Staff' ? '/orders' : '/'} replace />;

    const submit = async (target: HTMLFormElement) => {
        const data = new FormData(target);
        setSubmitting(true);
        setFailure(null);
        try {
            const { data: res } = await api.post<LoginResponse>('/auth/login', {
                email: String(data.get('email') ?? '').trim(),
                password: String(data.get('password') ?? ''),
            });
            dispatch(setCredentials({ user: res.user, token: res.access_token, refreshToken: res.refresh_token }));
        } catch (e) {
            const err = getApiError(e);
            setFailure(err.status === 401 ? 'invalid' : err.status === null ? 'network' : 'other');
            setSubmitting(false);
            email.current?.focus();
        }
    };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const found: Errors = {
            ...(String(data.get('email') ?? '').trim() ? {} : { email: t('login.required') }),
            ...(String(data.get('password') ?? '') ? {} : { password: t('login.required') }),
        };
        setErrors(found);
        if (found.email || found.password) {
            (found.email ? email : password).current?.focus();
            return;
        }
        void submit(e.currentTarget);
    };

    const banner = failure && t(`login.${failure}`);

    return (
        <div className="grid min-h-dvh bg-surface lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="flex flex-col px-4 pb-6 pt-6 sm:px-8 sm:pt-24 lg:pt-6">
                <Brand className="text-ticket text-strong sm:hidden" />
                <Brand className="hidden text-display text-strong sm:inline-flex lg:hidden" />
                <main className="mx-auto flex w-full max-w-[25rem] flex-1 flex-col justify-start pt-10 sm:pt-12 lg:justify-center lg:pt-0">
                    <form ref={form} onSubmit={onSubmit} noValidate className="flex flex-col gap-5" aria-busy={submitting || undefined}>
                        <h1 className="font-display text-display text-strong">{t('login.title')}</h1>
                        {session.notice === 'passwordChanged' && !failure && <InlineBanner tone="info">{t('login.passwordChanged')}</InlineBanner>}
                        {failure && (
                            <InlineBanner
                                tone="danger"
                                action={failure === 'network' ? { label: t('login.tryAgain'), onClick: () => form.current?.requestSubmit() } : undefined}
                            >
                                {banner}
                            </InlineBanner>
                        )}
                        <Field label={t('login.email')} error={errors.email}>
                            {(c) => (
                                <Input
                                    {...c}
                                    ref={email}
                                    onChange={() => setErrors((e) => ({ ...e, email: undefined }))}
                                    name="email"
                                    type="email"
                                    autoComplete="username"
                                    inputMode="email"
                                    autoCapitalize="none"
                                    spellCheck={false}
                                    readOnly={submitting}
                                    autoFocus
                                />
                            )}
                        </Field>
                        <Field label={t('login.password')} error={errors.password}>
                            {(c) => (
                                <Input
                                    {...c}
                                    ref={password}
                                    onChange={() => setErrors((e) => ({ ...e, password: undefined }))}
                                    name="password"
                                    type={shown ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    readOnly={submitting}
                                    trailing={
                                        <IconButton
                                            aria-label={shown ? t('login.hidePassword') : t('login.showPassword')}
                                            aria-pressed={shown}
                                            size="sm"
                                            onClick={() => setShown((v) => !v)}
                                        >
                                            {shown ? <EyeSlashIcon className="size-5" aria-hidden="true" /> : <EyeIcon className="size-5" aria-hidden="true" />}
                                        </IconButton>
                                    }
                                />
                            )}
                        </Field>
                        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
                            {t('login.submit')}
                        </Button>
                    </form>
                </main>
                <div className="mx-auto mt-8 flex w-full max-w-[25rem] flex-wrap items-center justify-between gap-3">
                    <LanguageSwitcher />
                    <ThemeSwitch />
                </div>
            </div>
            <aside className="hidden flex-col justify-between bg-brand p-12 text-on-brand lg:flex" aria-hidden="true">
                <Brand className="text-display" />
                <p className="max-w-[16ch] font-display text-display">{t('login.statement')}</p>
            </aside>
        </div>
    );
};

export default Login;
